import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import {
  ArrowRight,
  FileSpreadsheet,
  FileText,
  BarChart3,
  ScanLine,
  RotateCw,
  Columns3,
  CheckCircle2,
  ShieldCheck,
  Upload,
  Cpu,
  Sparkles,
  PenSquare,
  Camera,
  FileArchive,
} from "lucide-react";
import { InfoSections } from "@/components/InfoSections";
import { LANDING_SECTIONS } from "@/content/seoContent";

const TOOL_META = [
  { to: "/pdf-excel", icon: FileSpreadsheet, color: "emerald", key: "toolExcel" },
  { to: "/pdf-word", icon: FileText, color: "blue", key: "toolWord" },
  { to: "/immagine-pdf", icon: Camera, color: "amber", key: "toolImage" },
  { to: "/comprimi-pdf", icon: FileArchive, color: "rose", key: "toolCompress" },
  { to: "/modifica-pdf", icon: PenSquare, color: "featured", key: "toolEdit", featured: true },
  { to: "/analisi-excel", icon: BarChart3, color: "violet", key: "toolAnalysis" },
];
const STEP_ICONS = [Upload, Cpu, Sparkles];
const WHY_ICONS = [RotateCw, Columns3, ScanLine, CheckCircle2];
const colorMap = {
  emerald: "bg-emerald-100 text-emerald-600",
  blue: "bg-blue-100 text-blue-600",
  violet: "bg-violet-100 text-violet-600",
  amber: "bg-amber-100 text-amber-600",
  rose: "bg-rose-100 text-rose-600",
  featured: "bg-white/20 text-white",
};

export default function Landing() {
  const { landing: c, lang } = useLang();
  const seoSections = LANDING_SECTIONS[lang] || LANDING_SECTIONS.en;

  return (
    <div data-testid="landing-page">
      {/* Hero + feature grid — the central interface */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="dotec-grid-bg absolute inset-0 opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-3xl text-center dotec-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" />
              {c.suiteEyebrow}
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {c.suiteTitlePre}
              <span className="text-emerald-600">{c.suiteTitleHi}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              {c.suiteSub}
            </p>
          </div>

          {/* Feature cards grid: 5 cols desktop, 3 laptop, 2 tablet, 1 mobile */}
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TOOL_META.map((tool, i) => {
              const copy = c[tool.key];
              const featured = tool.featured;
              return (
                <div
                  key={tool.to}
                  data-testid={`feature-card-${tool.to.slice(1)}`}
                  className={`group relative flex flex-col rounded-3xl p-7 shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl ${
                    featured
                      ? "bg-gradient-to-br from-emerald-600 to-emerald-500 text-white shadow-emerald-600/20"
                      : "border border-slate-200 bg-white"
                  }`}
                >
                  {featured && (
                    <span className="absolute right-4 top-4 rounded-full bg-white/25 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white backdrop-blur">
                      {c.newBadge}
                    </span>
                  )}
                  <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${colorMap[tool.color]} transition-transform group-hover:scale-110`}>
                    <tool.icon className="h-8 w-8" />
                  </div>
                  <h3 className={`mt-6 font-display text-xl font-extrabold ${featured ? "text-white" : "text-slate-900"}`}>
                    {copy.title}
                  </h3>
                  <p className={`mt-2 flex-1 text-sm leading-relaxed ${featured ? "text-emerald-50" : "text-slate-500"}`}>
                    {copy.text}
                  </p>
                  <Link
                    to={tool.to}
                    data-testid={`feature-cta-${tool.to.slice(1)}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${
                      featured
                        ? "bg-white text-emerald-700 hover:bg-emerald-50"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {c.openTool}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2">
            {c.heroBadges.map((b) => (
              <span key={b} className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form informative sections (SEO / AdSense content) */}
      <div className="border-t border-slate-100 bg-white">
        <InfoSections sections={seoSections} testId="landing-seo-sections" />
      </div>

      {/* How it works */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              {c.howTitle}
            </h2>
            <p className="mt-3 text-lg text-slate-500">{c.howSub}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {c.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div
                  key={i}
                  className="rounded-3xl border border-slate-200 bg-slate-50 p-8 transition-all hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-slate-500">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why scans */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                {c.whyTitle}
              </h2>
              <p className="mt-5 leading-relaxed text-slate-600">{c.whyPara1}</p>
              <p className="mt-4 leading-relaxed text-slate-600">{c.whyPara2}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {c.whyBullets.map((w, i) => {
                const Icon = WHY_ICONS[i];
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6">
                    <Icon className="h-7 w-7 text-emerald-600" />
                    <h3 className="mt-4 font-display text-base font-bold text-slate-900">
                      {w.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{w.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* OCR precision stats */}
      <section className="bg-slate-900">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {c.ocrTitle}
            </h2>
            <p className="mt-4 leading-relaxed text-slate-300">{c.ocrPara}</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {c.ocrStats.map((s, i) => (
              <div key={i} className="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
                <p className="font-display text-4xl font-extrabold text-emerald-400">{s.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="border-t border-slate-200 bg-emerald-600">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-14 text-center lg:flex-row lg:px-8 lg:text-left">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white">
              {c.ctaTitle}
            </h2>
            <p className="mt-2 flex items-center justify-center gap-2 text-emerald-50 lg:justify-start">
              <ShieldCheck className="h-5 w-5" />
              {c.ctaSub}
            </p>
          </div>
          <Link
            to="/pdf-excel"
            data-testid="footer-cta-button"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-base font-bold text-emerald-700 shadow-lg transition-transform hover:scale-105"
          >
            {c.ctaBtn}
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
